/* eslint-disable react/no-unused-prop-types */
import { useState, useEffect, useCallback, useMemo } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { shape, arrayOf, string } from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
    Box,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core';
import { noop, filter, includes, map, get, uniqBy, debounce, sortBy, toLower, flow } from 'lodash';
import clsx from 'clsx';

import { filterPropDefaults, filterPropTypes } from '../../page-framework/filter/FilterPropTypes';
import { useEntityService } from '../../../services/EntityService';
import getEntityTagGroupFilterServiceFactory, {
    ENTITY_ID_FIELDS,
    ENTITY_LABEL_FIELDS,
    ENTITY_TYPE_TITLES,
    ENTITY_TYPE_TITLES_PLURAL,
    ENTITY_TYPE_MAPPING,
    FILTER_STATE_FIELDS,
} from './EntityTagGroupFilterServiceFactory';

const MAX_ITEMS_PER_INPUT = 20;
function canSelectMore(list) {
    return list.length < MAX_ITEMS_PER_INPUT;
}

const useStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,
        minWidth: '550px',
    },

    titleText: {
        '&:not(:first-child)': {
            paddingTop: theme.spacing(3),
        },
    },

    tagTextContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,
    },

    tagRadioGroupContainer: {
        paddingLeft: theme.spacing(1),
    },

    loadingContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentCenter,
        ...theme.mixins.flex.alignItemsCenter,
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),
    },

    autocompleteTextField: {
        '& > .MuiAutocomplete-inputRoot': {
            paddingRight: `${theme.spacing(0)} !important`,
        },
    },
}));

export default function EntityTagGroupFilter(props) {
    const { entityType, filterActions } = props;
    const stateService = getEntityTagGroupFilterServiceFactory(entityType)();
    const { state = stateService.createWithDefaultState() } = props;

    const classes = useStyles();

    const entityLabelField = ENTITY_LABEL_FIELDS[entityType];
    const entityIdField = ENTITY_ID_FIELDS[entityType];
    const entityTitle = i18n._(ENTITY_TYPE_TITLES[entityType]);
    const entityTitlePlural = i18n._(ENTITY_TYPE_TITLES_PLURAL[entityType]);
    const filterStateField = FILTER_STATE_FIELDS[entityType];

    const [tags, isLoadingTags] = useTags(entityType);
    const [entities, isLoadingEntities, loadMoreEntities, isLoadingMoreEntities] = useEntityType(
        entityType,
        state[filterStateField]
    );
    const loadMoreDebounced = useMemo(() => debounce(loadMoreEntities, 250), [loadMoreEntities]);
    const [entitySearch, setEntitySearch] = useState('');
    const onEntitySearchChange = (newSearch) => {
        setEntitySearch(newSearch);
        loadMoreDebounced(newSearch);
    };

    const isLoading = isLoadingTags || isLoadingEntities;

    const selectedTags = isLoadingTags ? [] : filter(tags, (tag) => includes(state.tags, tag.tag));
    const selectedEntities = isLoadingEntities
        ? []
        : filter(entities, (ent) => includes(state[filterStateField], ent[entityIdField]));

    const setSelectedTags = useCallback(
        (selTags) => filterActions.setState({ ...state, tags: map(selTags, 'tag') }),
        [filterActions, state]
    );

    const setSelectedEntities = useCallback(
        (selEnts) =>
            filterActions.setState({
                ...state,
                name: get(selEnts, '[0].name'),
                [filterStateField]: map(selEnts, entityIdField),
            }),
        [entityIdField, filterActions, filterStateField, state]
    );

    const handleTagOpChange = useCallback(
        (event) => filterActions.setState({ ...state, tagOp: event.target.value }),
        [filterActions, state]
    );

    const hasItems = !!selectedTags.length || !!selectedEntities.length;
    useEffect(() => {
        filterActions.setValid(hasItems);
    }, [filterActions, hasItems]);

    if (isLoading) {
        return (
            <Box width={400} className={classes.loadingContainer}>
                <CircularProgress size={16} />
                <Box ml={1}>
                    <Typography variant="subtitle2">
                        <Trans>Loading Data...</Trans>
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <div className={classes.root}>
            <TitleText className={classes.titleText}>
                <Trans>
                    {entityTitlePlural} ({selectedEntities.length} of {MAX_ITEMS_PER_INPUT}):
                </Trans>
            </TitleText>

            <AutocompleteTagField
                options={entities}
                autoFocus
                value={selectedEntities}
                inputValue={entitySearch}
                labelField={entityLabelField}
                onChange={setSelectedEntities}
                placeholder={t`Add ${entityTitle}`}
                onInputChange={onEntitySearchChange}
                isLoading={isLoadingMoreEntities}
            />
            <TitleText className={clsx(classes.tagTextContainer, classes.titleText)}>
                <Trans>
                    Tags ({selectedTags.length} of {MAX_ITEMS_PER_INPUT}):{' '}
                </Trans>

                <RadioGroup
                    value={state.tagOp}
                    onChange={handleTagOpChange}
                    className={classes.tagRadioGroupContainer}
                    row
                >
                    <FormControlLabel value="AND" label={i18n._(t`All`)} control={<Radio />} />
                    <FormControlLabel value="OR" label={i18n._(t`Any`)} control={<Radio />} />
                </RadioGroup>
            </TitleText>

            <AutocompleteTagField
                options={tags}
                value={selectedTags}
                labelField="tag"
                onChange={setSelectedTags}
                placeholder={t`Add Tag`}
                onInputChange={noop}
            />
        </div>
    );
}

function AutocompleteTagField(options) {
    const {
        options: acOptions,
        value,
        onChange,
        inputValue,
        onInputChange,
        labelField,
        placeholder,
        isLoading = false,
        autoFocus = false,
    } = options;
    const classes = useStyles();

    const onChangeCallback = useCallback((_event, selectionValue) => onChange(selectionValue), [onChange]);

    const onInputChangeCallback = useCallback(
        (event, selectionValue) => {
            if (event) onInputChange(selectionValue);
        },
        [onInputChange]
    );

    return (
        <Autocomplete
            multiple
            autoHighlight
            filterSelectedOptions
            options={acOptions}
            value={value}
            onChange={onChangeCallback}
            // default value is set as well as input value
            // due to a bug when using autocomplete as a controlled component
            // wherein the input value is immediately cleared on input unless
            // a default value is specified
            defaultValue={inputValue}
            inputValue={inputValue}
            getOptionDisabled={() => !canSelectMore(value)}
            onInputChange={onInputChangeCallback}
            getOptionLabel={(o) => o?.[labelField]}
            noOptionsText={i18n._(t`No Results`)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    placeholder={i18n._(placeholder)}
                    className={classes.autocompleteTextField}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: isLoading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={16} />
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
            )}
        />
    );
}

function TitleText(props) {
    const { children, ...rest } = props;

    return (
        <Typography component="div" variant="subtitle1" {...rest}>
            {children}
        </Typography>
    );
}

function useTags(entityType) {
    const [tags, setTags] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const type = ENTITY_TYPE_MAPPING[entityType];
    const entityService = useEntityService();

    useEffect(() => {
        entityService.getEntities('tags', { rowLimit: 1000, type }).then(({ data }) => {
            setTags(data);
            setIsLoading(false);
        });
    }, [type, entityService]);

    return [tags, isLoading];
}

function useEntityType(entityType, initialEntities) {
    const [entities, setEntities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const entityService = useEntityService();

    useEffect(() => {
        entityService
            .getEntities(ENTITY_TYPE_MAPPING[entityType], {
                rowLimit: 100,
                sort: 'name',
            })
            .then(({ data }) => {
                return Promise.all(initialEntities.map((id) => entityService.getEntity(entityType, id))).then(
                    (resolvedEntities) => setEntities(processEntities(data, resolvedEntities))
                );
            })
            .then(() => setIsLoading(false));
    }, [entityType, entityService]); // eslint-disable-line react-hooks/exhaustive-deps
    // We dont want to rerun the effects if initialEntities changes since we only want it to
    // run when the filter is first loaded into the ui

    const loadMore = useCallback(
        (search) => {
            setIsLoadingMore(true);
            entityService
                .getEntities(ENTITY_TYPE_MAPPING[entityType], { rowLimit: 100, sort: 'name', search })
                .then(({ data }) => setEntities(processEntities(entities, data)))
                .then(() => setIsLoadingMore(false));
        },
        [entities, entityService, entityType, setIsLoadingMore]
    );

    return [entities, isLoading, loadMore, isLoadingMore];
}

const processEntities = flow(
    (oldEntities, newEntities) => uniqBy([...oldEntities, ...newEntities], 'uuid'),
    (entities) => filter(entities, (entity) => !!entity.name),
    (entities) => sortBy(entities, (entity) => toLower(entity?.name))
);

EntityTagGroupFilter.propTypes = {
    entityType: string.isRequired,
    id: filterPropTypes.id,
    title: filterPropTypes.title,
    filterActions: filterPropTypes.filterActions,
    state: shape({
        tags: arrayOf(string).isRequired,
        tagOp: string.isRequired,
    }),
};

EntityTagGroupFilter.defaultProps = {
    ...filterPropDefaults,

    state: undefined,
};
